import { useState, useEffect } from 'react'
import {
  DialogRoot,
  DialogBackdrop,
  DialogPositioner,
  DialogContent,
  DialogCloseTrigger,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  Button,
  VStack,
  Input,
  Text,
  Box,
} from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import { Field } from '@/components/ui/field'
import { NativeSelectRoot, NativeSelectField } from '@/components/ui/native-select'
import {
  useCreateBudgetLine,
  useUpdateBudgetLine,
  useDistributionKeys,
} from '../hooks'
import type { BudgetLine, BudgetLineInsert } from '../types'

interface BudgetLineModalProps {
  isOpen: boolean
  onClose: () => void
  budgetId: string
  residenceId: string
  budgetLine?: BudgetLine | null // For editing
}

export function BudgetLineModal({
  isOpen,
  onClose,
  budgetId,
  residenceId,
  budgetLine,
}: BudgetLineModalProps) {
  const { t } = useTranslation()
  const isEditing = !!budgetLine

  // Form state
  // Note: Le compte de charges est déterminé automatiquement par le type de budget
  // (701 pour charges courantes, 102 pour travaux, 105 pour fonds ALUR)
  const [distributionKeyId, setDistributionKeyId] = useState('')
  const [label, setLabel] = useState('')
  const [budgetedAmount, setBudgetedAmount] = useState('')

  // Queries
  const { data: distributionKeys } = useDistributionKeys(residenceId)

  // Mutations
  const createBudgetLine = useCreateBudgetLine()
  const updateBudgetLine = useUpdateBudgetLine()

  // Initialize form when editing
  useEffect(() => {
    if (budgetLine) {
      setDistributionKeyId(budgetLine.distribution_key_id ?? '')
      setLabel(budgetLine.label ?? '')
      setBudgetedAmount(budgetLine.budgeted_amount?.toString() ?? '')
    } else {
      resetForm()
    }
  }, [budgetLine, isOpen])

  const resetForm = () => {
    setDistributionKeyId('')
    setLabel('')
    setBudgetedAmount('')
  }

  const handleClose = () => {
    resetForm()
    onClose()
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const data: Omit<BudgetLineInsert, 'residence_chart_account_id'> = {
      budget_id: budgetId,
      distribution_key_id: distributionKeyId,
      label,
      budgeted_amount: parseFloat(budgetedAmount) || 0,
    }

    try {
      if (isEditing && budgetLine) {
        await updateBudgetLine.mutateAsync({
          id: budgetLine.id,
          ...data,
        })
      } else {
        await createBudgetLine.mutateAsync(data as BudgetLineInsert)
      }
      handleClose()
    } catch (error) {
      console.error('Error saving budget line:', error)
    }
  }

  const isValid =
    distributionKeyId &&
    label.trim() &&
    parseFloat(budgetedAmount) > 0

  const isPending = createBudgetLine.isPending || updateBudgetLine.isPending

  return (
    <DialogRoot open={isOpen} onOpenChange={(e) => !e.open && handleClose()} size="md">
      <DialogBackdrop />
      <DialogPositioner>
        <DialogContent maxW="550px">
          <DialogCloseTrigger />
          <DialogHeader>
            <DialogTitle>
              {isEditing ? 'Modifier le poste de coût' : 'Ajouter un poste de coût'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit}>
            <DialogBody>
              <VStack gap={4} align="stretch">
                {/* Libellé */}
                <Field label="Libellé" required>
                  <Input
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                    placeholder="Ex: Entretien parties communes"
                  />
                </Field>

                {/* Clé de répartition */}
                <Field label="Clé de répartition" required>
                  <NativeSelectRoot>
                    <NativeSelectField
                      value={distributionKeyId}
                      onChange={(e) => setDistributionKeyId(e.target.value)}
                    >
                      <option value="">Sélectionner une clé</option>
                      {(distributionKeys ?? []).map((key) => (
                        <option key={key.id} value={key.id}>
                          {key.name} ({key.key_type === 'general' ? 'Générale' : 'Spéciale'})
                        </option>
                      ))}
                    </NativeSelectField>
                  </NativeSelectRoot>
                  {(distributionKeys ?? []).length === 0 && (
                    <Text fontSize="sm" color="orange.500" mt={1}>
                      Aucune clé de répartition configurée. Créez-en une dans Paramètres &gt; Clés de répartition.
                    </Text>
                  )}
                </Field>

                {/* Montant prévisionnel */}
                <Field label="Montant prévisionnel annuel" required>
                  <Box position="relative">
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={budgetedAmount}
                      onChange={(e) => setBudgetedAmount(e.target.value)}
                      placeholder="0.00"
                      pr="40px"
                    />
                    <Text
                      position="absolute"
                      right={3}
                      top="50%"
                      transform="translateY(-50%)"
                      color="gray.500"
                      fontSize="sm"
                    >
                      €
                    </Text>
                  </Box>
                </Field>

                {/* Info sur le compte automatique */}
                <Box p={3} bg="blue.50" borderRadius="md">
                  <Text fontSize="sm" color="blue.700">
                    Le compte comptable sera automatiquement déterminé selon le type de budget
                    (701 pour charges courantes, 102 pour travaux, 105 pour fonds ALUR).
                  </Text>
                </Box>
              </VStack>
            </DialogBody>

            <DialogFooter>
              <Button variant="ghost" onClick={handleClose}>
                {t('common.cancel')}
              </Button>
              <Button
                type="submit"
                colorPalette="brand"
                loading={isPending}
                disabled={!isValid || isPending}
              >
                {isEditing ? t('common.save') : t('common.add')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </DialogPositioner>
    </DialogRoot>
  )
}
